import { useEffect, useRef, useState } from 'react';
import type { LineMessageView } from '@app/shared';
import { fetchLineAttachmentUrl } from './lineApi';

interface Props {
  messages: LineMessageView[];
}

export function MessageStream({ messages }: Props): JSX.Element {
  const bottomRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
  }, [messages.length]);

  if (messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-slate-500">
        尚無訊息
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto px-3 py-2 bg-slate-50 min-h-0">
      <div className="flex flex-col gap-2">
        {messages.map((m) => (
          <MessageBubble key={m.id} m={m} />
        ))}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}

function MessageBubble({ m }: { m: LineMessageView }): JSX.Element {
  const isIn = m.direction === 'IN';
  const align = isIn ? 'items-start' : 'items-end';
  const isSticker = m.messageType === 'STICKER';
  const bubbleColor = isIn
    ? 'bg-white text-slate-800 border'
    : m.senderType === 'AI'
      ? 'bg-emerald-100 text-emerald-900'
      : m.senderType === 'AGENT'
        ? 'bg-blue-600 text-white'
        : 'bg-slate-200 text-slate-700';
  // 貼圖讓圖片直接浮在背景上,不要 bubble 邊框 / 底色 / padding(對齊 LINE 原生 UI)
  const bubbleClass = isSticker
    ? 'max-w-[80%]'
    : `max-w-[80%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${bubbleColor}`;
  return (
    <div className={`flex flex-col ${align}`}>
      <div className="text-[10px] text-slate-500 px-1">
        {labelOf(m)} · {new Date(m.createdAt).toLocaleString('zh-TW')}
      </div>
      <div className={bubbleClass}>{renderBody(m)}</div>
    </div>
  );
}

function labelOf(m: LineMessageView): string {
  if (m.direction === 'IN') return '客戶';
  if (m.senderType === 'AI') return 'AI';
  if (m.senderType === 'AGENT') return `客服${m.agentUserName ? '・' + m.agentUserName : ''}`;
  return '系統';
}

function renderBody(m: LineMessageView): JSX.Element {
  switch (m.messageType) {
    case 'TEXT':
    case 'SYSTEM':
      return <>{m.text ?? ''}</>;
    case 'STICKER':
      // LINE 公開 sticker CDN;靜態 PNG,動態貼圖也能用(僅顯示第一幀)
      return m.stickerStickerId ? (
        <img
          src={`https://stickershop.line-scdn.net/stickershop/v1/sticker/${m.stickerStickerId}/iPhone/sticker.png`}
          alt={`貼圖 ${m.stickerStickerId}`}
          title={`pkg=${m.stickerPackageId ?? '?'} id=${m.stickerStickerId}`}
          className="w-[140px] h-[140px] object-contain"
          referrerPolicy="no-referrer"
        />
      ) : (
        <span className="text-xs italic">[貼圖]</span>
      );
    case 'IMAGE':
      return m.attachment ? (
        <LineImageAttachment uuid={m.attachment.uuid} alt={m.attachment.originalName} />
      ) : (
        <span className="text-xs italic">[圖片(未取得)]</span>
      );
    case 'VIDEO':
    case 'AUDIO':
    case 'FILE':
      return m.attachment ? (
        <LineFileAttachment
          uuid={m.attachment.uuid}
          name={m.attachment.originalName}
          kind={m.messageType === 'VIDEO' ? '影片' : m.messageType === 'AUDIO' ? '語音' : '檔案'}
        />
      ) : (
        <span className="text-xs italic">[{m.messageType}(未取得)]</span>
      );
    default:
      return <>{m.text ?? ''}</>;
  }
}

/**
 * LINE 附件圖片:`<img>` 不會自動帶 JWT,所以要先 axios 拿 signed URL,
 * 再放進 src(沿用客戶圖片牆 InterviewImageWall 同樣的模式)。
 */
function LineImageAttachment({ uuid, alt }: { uuid: string; alt: string }): JSX.Element {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setUrl(null);
    setError(null);
    fetchLineAttachmentUrl(uuid)
      .then((u) => {
        if (!cancelled) setUrl(u);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : '載入失敗');
      });
    return () => {
      cancelled = true;
    };
  }, [uuid]);

  if (error) {
    return <span className="text-xs italic text-red-200">[圖片載入失敗:{error}]</span>;
  }
  if (!url) {
    return <span className="text-xs italic">[圖片載入中…]</span>;
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" className="block">
      <img
        src={url}
        alt={alt}
        className="max-w-[260px] max-h-[260px] rounded"
        referrerPolicy="no-referrer"
      />
    </a>
  );
}

/**
 * LINE 附件非圖片:點擊時取 signed URL 再導向(沿用 attachments downloadAttachment 模式)。
 */
function LineFileAttachment({
  uuid,
  name,
  kind,
}: {
  uuid: string;
  name: string;
  kind: string;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const onClick = async (e: React.MouseEvent): Promise<void> => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const url = await fetchLineAttachmentUrl(uuid);
      window.open(url, '_blank', 'noreferrer');
    } catch (err) {
      window.alert(`下載失敗:${err instanceof Error ? err.message : '未知錯誤'}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <a
      href="#"
      onClick={(e) => void onClick(e)}
      className="underline"
    >
      {busy ? `[${kind}] 取得連結中…` : `[${kind}] ${name}`}
    </a>
  );
}
